import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { fetchContent, fetchProducts, getImageUrl, submitContact } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import { useUser } from '../context/UserContext';
import { withHomeDefaults } from '../data/homeDefaults';
import { reply, fillVars, buildEnquiryMessage } from '../utils/chatbotEngine';
import './ChatBot.css';

// Site assistant: a sticky round icon (bottom-right, under the WhatsApp button) that opens a chat window.
// Answers come from the rule-based engine in utils/chatbotEngine.js, using the Q&A configured in
// Admin → Home → Chatbot plus live shop data (products, contact details).

const STORE_KEY = 'gw_chat_v1';
const TEASER_KEY = 'gw_chat_teaser_seen';
const SENT_KEY = 'gw_chat_last_enquiry';          // time of the last message sent to the team (30s cool-down)
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const EMPTY_SUPPORT = { name: '', email: '', phone: '', message: '', hp: '', status: 'idle', error: '' };
const CONTACT_DEFAULTS = {
  phone: '087 100 2241',
  email: 'sales@greenwheelsscooters.ie',
  address: 'Unit 6, Park Shopping Centre, Prussia Street, Dublin 7, D07K802, Ireland',
  hours: '10:30am - 7:00pm, Mon - Sun',
};

const safeGet = (storage, key) => { try { return storage.getItem(key); } catch { return null; } };
const safeSet = (storage, key, val) => { try { storage.setItem(key, val); } catch { /* storage blocked */ } };

const RobotIcon = ({ size = 30 }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden="true">
    <line x1="24" y1="5" x2="24" y2="11" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" />
    <circle cx="24" cy="4.5" r="2.6" fill="#fff" />
    <rect x="8" y="11" width="32" height="26" rx="10" fill="#fff" />
    <rect x="12.5" y="16" width="23" height="14" rx="6.5" fill="#12352f" />
    <circle cx="19.5" cy="22.5" r="2.6" fill="#00e5a0" />
    <circle cx="28.5" cy="22.5" r="2.6" fill="#00e5a0" />
    <path d="M20 27.2c1.2 1.1 2.5 1.6 4 1.6s2.8-.5 4-1.6" fill="none" stroke="#00e5a0" strokeWidth="1.8" strokeLinecap="round" />
    <rect x="3" y="19" width="4" height="9" rx="2" fill="#fff" />
    <rect x="41" y="19" width="4" height="9" rx="2" fill="#fff" />
    <path d="M17 37v3.5a3 3 0 003 3h8a3 3 0 003-3V37" fill="#fff" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor"><path d="M3.4 20.4l17.45-7.48a1 1 0 000-1.84L3.4 3.6a.993.993 0 00-1.39.91L2 9.12c0 .5.37.93.87.99L17 12 2.87 13.88c-.5.07-.87.5-.87 1l.01 4.61c0 .71.73 1.2 1.39.91z" /></svg>
);

let idCounter = 0;
const newId = () => `m${Date.now().toString(36)}${idCounter++}`;

export default function ChatBot() {
  const currency = useCurrency();
  const userCtx = useUser();
  const user = userCtx?.user;
  const [support, setSupport] = useState(EMPTY_SUPPORT);   // the "message our team" form shown inside the chat
  const [cfg, setCfg] = useState(() => withHomeDefaults({}).chatbot);
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => {
    try { return JSON.parse(safeGet(sessionStorage, STORE_KEY) || '[]'); } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const dataRef = useRef({ products: [], contact: CONTACT_DEFAULTS, promise: null });
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  // Admin-managed settings (home content → chatbot); defaults apply until/unless it loads
  useEffect(() => {
    fetchContent('home').then(d => setCfg(withHomeDefaults(d).chatbot)).catch(() => {});
  }, []);

  // Teaser bubble ("Questions about delivery?") once per visit, a few seconds after load
  useEffect(() => {
    if (!cfg.enabled || !cfg.showTeaser || !cfg.teaser || safeGet(sessionStorage, TEASER_KEY)) return;
    const t = setTimeout(() => setTeaser(true), 4000);
    return () => clearTimeout(t);
  }, [cfg.enabled, cfg.showTeaser, cfg.teaser]);

  // Products + contact details are loaded the first time the chat opens (not on every page view)
  const ensureData = useCallback(() => {
    const d = dataRef.current;
    if (!d.promise) {
      d.promise = Promise.all([
        fetchProducts().catch(() => []),
        fetchContent('contact').catch(() => ({})),
      ]).then(([products, contact]) => {
        d.products = Array.isArray(products) ? products : [];
        d.contact = { ...CONTACT_DEFAULTS, ...(contact || {}) };
      });
    }
    return d.promise;
  }, []);

  const toggle = () => {
    setOpen(o => !o);
    setTeaser(false);
    safeSet(sessionStorage, TEASER_KEY, '1');
  };
  const closeTeaser = (e) => { e.stopPropagation(); setTeaser(false); safeSet(sessionStorage, TEASER_KEY, '1'); };

  useEffect(() => {
    if (!open) return;
    ensureData();
    const t = setTimeout(() => inputRef.current?.focus(), 150);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [open, ensureData]);

  // persist the conversation for this visit, and keep the newest message in view
  useEffect(() => { safeSet(sessionStorage, STORE_KEY, JSON.stringify(messages.filter(m => !m.form).slice(-40))); }, [messages]);
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, typing, open]);

  const send = async (raw) => {
    const text = String(raw || '').trim();
    if (!text || typing) return;
    setInput('');
    setMessages(m => [...m, { id: newId(), from: 'user', text }]);
    setTyping(true);
    await ensureData();
    const { products, contact } = dataRef.current;
    const answer = reply(text, {
      faqs: cfg.faqs, fallback: cfg.fallback, quickReplies: cfg.quickReplies,
      supportEnabled: cfg.supportEnabled !== false, supportIntro: cfg.supportIntro,
      products, currency,
      vars: { phone: contact.phone, email: contact.email, address: contact.address, hours: contact.hours },
    });
    await new Promise(r => setTimeout(r, 450 + Math.random() * 350));   // a short "typing…" pause feels natural
    setTyping(false);
    if (answer) setMessages(m => [...m, { id: newId(), from: 'bot', ...answer, asked: text }]);
  };

  const onSubmit = (e) => { e.preventDefault(); send(input); };

  // ── Customer support: send a message to the team ──
  // Uses the same endpoint as the Contact Us page, so the enquiry appears in Admin → Enquiries and the
  // admin gets the usual email / WhatsApp notification. The recent chat is attached for context.
  const startSupport = (asked) => {
    const phone = user?.mobile || user?.phone || '';
    setSupport({ ...EMPTY_SUPPORT, name: user?.name || '', email: user?.email || '', phone, message: asked || '' });
    setMessages(m => [
      ...m.filter(x => !x.form),
      { id: newId(), from: 'bot', form: true, text: '' },
    ]);
  };
  const cancelSupport = () => setMessages(m => m.filter(x => !x.form));
  const setField = (k) => (e) => setSupport(s => ({ ...s, [k]: e.target.value, error: '' }));

  const submitSupport = async (e) => {
    e.preventDefault();
    if (support.status === 'sending') return;
    const finish = (name) => setMessages(m => [
      ...m.filter(x => !x.form),
      { id: newId(), from: 'bot', text: fillVars(cfg.supportSuccess, { name: name || 'there' }), link: { label: 'Contact page', to: '/contact' }, chips: cfg.quickReplies },
    ]);
    if (support.hp) { finish(support.name); return; }   // hidden field filled in = a bot, pretend it worked
    const name = support.name.trim(), email = support.email.trim(), phone = support.phone.trim(), message = support.message.trim();
    if (!name) return setSupport(s => ({ ...s, error: 'Please enter your name.' }));
    if (!EMAIL_RE.test(email)) return setSupport(s => ({ ...s, error: 'Please enter a valid email address.' }));
    if (phone.replace(/\D/g, '').length < 7) return setSupport(s => ({ ...s, error: 'Please enter a phone number we can reach you on.' }));
    if (message.length < 3) return setSupport(s => ({ ...s, error: 'Please tell us how we can help.' }));
    if (Date.now() - Number(safeGet(sessionStorage, SENT_KEY) || 0) < 30000) {
      return setSupport(s => ({ ...s, error: 'Your last message was just sent — please wait a few seconds before sending another.' }));
    }
    setSupport(s => ({ ...s, status: 'sending', error: '' }));
    try {
      await submitContact({ name, email, phone, message: buildEnquiryMessage(message, messages) });
      safeSet(sessionStorage, SENT_KEY, String(Date.now()));
      setSupport(EMPTY_SUPPORT);
      finish(name);
    } catch {
      const tel = dataRef.current.contact.phone;
      setSupport(s => ({ ...s, status: 'idle', error: `Sorry, we couldn’t send that. Please try again, or call us on ${tel}.` }));
    }
  };
  const clearChat = () => { setMessages([]); safeSet(sessionStorage, STORE_KEY, '[]'); };
  const onLinkClick = () => { if (window.innerWidth <= 768) setOpen(false); };   // phones: reveal the page being opened

  if (!cfg.enabled) return null;

  const lastBotIdx = [...messages].reverse().findIndex(m => m.from === 'bot');
  const lastBot = lastBotIdx === -1 ? null : messages[messages.length - 1 - lastBotIdx];
  const showWelcomeChips = messages.length === 0;
  const chips = typing ? [] : showWelcomeChips ? cfg.quickReplies : (lastBot && messages[messages.length - 1] === lastBot ? lastBot.chips : []);
  const waNumber = String(cfg.whatsapp || '').replace(/\D/g, '');
  const waLink = (q) => `https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi Green Wheels Scooters, ${q || 'I have a question.'}`)}`;

  const renderLink = (l) => {
    if (!l?.to) return null;
    return /^https?:\/\//.test(l.to)
      ? <a className="gwc-link" href={l.to} target="_blank" rel="noopener noreferrer">{l.label} &rarr;</a>
      : <Link className="gwc-link" to={l.to} onClick={onLinkClick}>{l.label} &rarr;</Link>;
  };

  return (
    <>
      {teaser && !open && (
        <div className="gwc-teaser" role="status">
          <button type="button" className="gwc-teaser-main" onClick={toggle}>{cfg.teaser}</button>
          <button type="button" className="gwc-teaser-x" onClick={closeTeaser} aria-label="Dismiss">&times;</button>
        </div>
      )}

      <button
        type="button"
        className={`gwc-launcher${open ? ' is-open' : ''}`}
        onClick={toggle}
        aria-label={open ? 'Close chat' : `Open chat with ${cfg.botName}`}
        aria-expanded={open}
      >
        {open
          ? <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" /></svg>
          : <RobotIcon size={32} />}
        {!open && <span className="gwc-online" aria-hidden="true" />}
      </button>

      {open && (
        <section className="gwc-panel" role="dialog" aria-label={`Chat with ${cfg.botName}`}>
          <header className="gwc-head">
            <span className="gwc-avatar"><RobotIcon size={26} /></span>
            <div className="gwc-head-text">
              <strong>{cfg.botName}</strong>
              <span><i aria-hidden="true" /> Online · replies instantly</span>
            </div>
            {messages.length > 0 && <button type="button" className="gwc-head-btn" onClick={clearChat} title="Start a new chat">New chat</button>}
            <button type="button" className="gwc-close" onClick={() => setOpen(false)} aria-label="Close chat">&times;</button>
          </header>

          <div className="gwc-body" ref={bodyRef} role="log" aria-live="polite">
            <div className="gwc-row bot">
              <span className="gwc-mini"><RobotIcon size={16} /></span>
              <div className="gwc-bubble">{cfg.welcome}</div>
            </div>

            {messages.map(m => (
              <div key={m.id} className={`gwc-row ${m.from}`}>
                {m.from === 'bot' && <span className="gwc-mini"><RobotIcon size={16} /></span>}
                <div className="gwc-stack">
                  {!m.form && <div className="gwc-bubble" style={{ whiteSpace: 'pre-line' }}>{m.text}</div>}

                  {m.form && (
                    <form className="gwc-support" onSubmit={submitSupport} noValidate>
                      <strong className="gwc-support-title">Message our team</strong>
                      <span className="gwc-support-note">We’ll reply by email or phone as soon as we can.</span>
                      <input type="text" placeholder="Your name" autoComplete="name" value={support.name} onChange={setField('name')} required aria-label="Your name" />
                      <input type="email" placeholder="Email address" autoComplete="email" value={support.email} onChange={setField('email')} required aria-label="Email address" />
                      <input type="tel" placeholder="Phone number" autoComplete="tel" value={support.phone} onChange={setField('phone')} required aria-label="Phone number" />
                      <textarea placeholder="How can we help?" rows={3} maxLength={1500} value={support.message} onChange={setField('message')} required aria-label="Your message" />
                      {/* honeypot: hidden from people, filled in by spam bots */}
                      <input className="gwc-hp" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={support.hp} onChange={setField('hp')} name="website" />
                      {support.error && <p className="gwc-support-error" role="alert">{support.error}</p>}
                      <div className="gwc-support-actions">
                        <button type="submit" className="gwc-support-send" disabled={support.status === 'sending'}>{support.status === 'sending' ? 'Sending…' : 'Send to our team'}</button>
                        <button type="button" className="gwc-support-cancel" onClick={cancelSupport} disabled={support.status === 'sending'}>Cancel</button>
                      </div>
                    </form>
                  )}

                  {m.products?.length > 0 && (
                    <div className="gwc-products">
                      {m.products.map(p => (
                        <Link key={p.id} to={p.to} className="gwc-product" onClick={onLinkClick}>
                          <span className="gwc-product-img">
                            {p.image && <img src={getImageUrl(p.image, 120)} alt="" loading="lazy" width="56" height="56" onError={e => { e.target.style.display = 'none'; }} />}
                          </span>
                          <span className="gwc-product-info">
                            <span className="gwc-product-name">{p.name}</span>
                            <span className="gwc-product-price">{p.price}{p.oldPrice && <s>{p.oldPrice}</s>}</span>
                            {!p.inStock && <span className="gwc-product-out">Out of stock</span>}
                          </span>
                          <span className="gwc-product-go" aria-hidden="true">&rsaquo;</span>
                        </Link>
                      ))}
                    </div>
                  )}

                  {m.from === 'bot' && renderLink(m.link)}

                  {(m.handoff || m.fallback) && (
                    <div className="gwc-handoff">
                      {cfg.supportEnabled !== false && m.handoff && (
                        <button type="button" className="gwc-team" onClick={() => startSupport(m.asked)}>✉ Message our team</button>
                      )}
                      <a className="gwc-wa" href={waLink(m.asked)} target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a>
                      {m.fallback && <Link className="gwc-contact" to="/contact" onClick={onLinkClick}>Contact us</Link>}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {typing && (
              <div className="gwc-row bot">
                <span className="gwc-mini"><RobotIcon size={16} /></span>
                <div className="gwc-bubble gwc-typing" aria-label="Typing"><i /><i /><i /></div>
              </div>
            )}

            {chips?.length > 0 && (
              <div className="gwc-chips">
                {chips.map(c => <button key={c} type="button" className="gwc-chip" onClick={() => send(c)}>{c}</button>)}
              </div>
            )}
          </div>

          <form className="gwc-form" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type your question…"
              aria-label="Type your question"
              maxLength={300}
              autoComplete="off"
            />
            <button type="submit" className="gwc-send" disabled={!input.trim() || typing} aria-label="Send"><SendIcon /></button>
          </form>
        </section>
      )}
    </>
  );
}
